import React from "react";
import { View, StyleSheet, Platform, Pressable } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { useEffect, useState } from "react";
import { LeftArrow, MagnifyingGlass } from "../../../components/svgs/common";
import { MinusCircleIcon, PlusCircle } from "../../../components/svgIcons";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import {
  Degree,
  GetDegreesBySchoolByQueryDocument,
  useGetStudentsByDegreeIdQuery,
  useInviteUsersToEnrollInDegreeMutation,
  useRemoveStudentsFromDegreeMutation,
} from "../../../graphql/generated/graphql";
import NewCustomAxioActionModal from "../UsersTabContainer/NewCustomAxioActionModal";
import { NewCustomActionButton } from "../../../components/common/general/CustomButton/NewCustomActionButton";
import DegreeRosterTable from "./DegreeRosterTable";
import InviteStudentModalContainer from "../../courseSettings/RosterManagementContainer/InviteStudentModalContainer";
import { useAppConfig } from "../../../AppConfigProvider";
import usePagination from "../../../hooks/usePagination";
import TableControlsContainer from "../../general/TableControls/TableControlsContainer";
import useSortingDropdown, { SORT_ITEMS } from "../../../hooks/useSortingDropdown";
import SortingDropdown from "../../../components/common/TableControls/SortingDropdown/SortingDropdown";
import CustomSearchInput from "../../../components/common/general/CustomSearchInput/CustomSearchInput";

const modals = {
  removeStudents: {
    title: "Remove Selected Students from the Degree?",
    ariaLabel: "Remove Students From Degree Modal",
    warningText:
      "This action cannot be undone. These students will lose access to degree materials and activities immediately.",
    actionButtonText: "Remove Students",
  },
  removeConfirmation: {
    title: "Students Removed",
    ariaLabel: "Students Removed Modal",
    warningText: "The selected students have been removed from the degree.",
    actionButtonText: "OK",
  },
};

export enum DegreeRosterModalState {
  CLOSED = "CLOSED",
  INVITE_STUDENTS = "INVITE_STUDENTS",
  REMOVE_STUDENTS = "REMOVE_STUDENTS",
  REMOVE_CONFIRMATION = "REMOVE_CONFIRMATION",
}

interface DegreeRosterTabContainerProps {
  degree: Pick<Degree, "id" | "name">;
  onBack: () => void;
}

const DegreeRosterTabContainer: React.FC<DegreeRosterTabContainerProps> = ({ degree, onBack }) => {
  const { primaryColor, axioSchools } = useAppConfig();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [modalState, setModalState] = useState<{
    type: DegreeRosterModalState;
  }>({
    type: DegreeRosterModalState.CLOSED,
  });

  const { sortBy, setSortBy, sortDropdownOpen, setSortDropdownOpen } = useSortingDropdown({
    defaultSortBy: "name_asc",
  });

  const { page, setPage, pageSize, setPageSize } = usePagination({});

  const { data: getStudentsByDegreeIdData, refetch: getStudentsByDegreeIdRefetch } = useGetStudentsByDegreeIdQuery({
    variables: {
      degreeId: degree.id,
      query: searchQuery,
      page: page,
      pageSize: pageSize,
      sortBy,
    },
    skip: !degree.id,
  });

  const totalItemsCount = getStudentsByDegreeIdData?.getStudentsByDegreeId.count || 0;

  const [inviteUsersToEnrollInDegree] = useInviteUsersToEnrollInDegreeMutation();
  const [removeStudentsFromDegree] = useRemoveStudentsFromDegreeMutation();

  const schoolId = axioSchools[0];

  const inviteStudents = async (students: { name: string; email: string }[]) => {
    setIsSubmitting(true);
    try {
      const result = await inviteUsersToEnrollInDegree({
        variables: {
          input: {
            degreeId: degree.id,
            users: students,
          },
        },
        refetchQueries: [
          {
            query: GetDegreesBySchoolByQueryDocument,
            variables: {
              schoolId,
              query: searchQuery,
              page: page,
              pageSize: pageSize,
            },
          },
        ],
      });

      if (result.data?.inviteUsersToEnrollInDegree.success) {
        getStudentsByDegreeIdRefetch();
        setIsSubmitting(false);
        return true;
      }
      setIsSubmitting(false);
      return false;
    } catch (error) {
      setIsSubmitting(false);
      return false;
    }
  };

  const handleRemoveStudents = async () => {
    if (!selectedStudents.length) return;

    try {
      await removeStudentsFromDegree({
        variables: {
          input: {
            degreeId: degree.id,
            studentIds: selectedStudents,
          },
        },
        refetchQueries: [
          {
            query: GetDegreesBySchoolByQueryDocument,
            variables: {
              schoolId,
              query: searchQuery,
              page: page,
              pageSize: pageSize,
            },
          },
        ],
      });
      getStudentsByDegreeIdRefetch();
      setSelectedStudents([]);
      setModalState({ type: DegreeRosterModalState.REMOVE_CONFIRMATION });
    } catch (error) {
      console.error("Error removing students:", error);
    }
  };

  const handleModalClose = () => {
    setModalState({ type: DegreeRosterModalState.CLOSED });
  };

  useEffect(() => {
    setPage(1);
  }, [searchQuery, sortBy]);

  useEffect(() => {
    getStudentsByDegreeIdRefetch();
  }, [searchQuery, page, pageSize, sortBy]);

  return (
    <>
      <View style={styles.container}>
        <View style={styles.header}>
          <Pressable style={styles.backButton} onPress={onBack}>
            <LeftArrow />
            <CustomText text="Back" size="m" weight="semiBold" style={styles.backText} onPress={onBack} />
          </Pressable>
          <View style={styles.headerRow}>
            <View style={styles.subHeaderContainer}>
              <CustomText
                text={degree.name}
                size="l"
                weight="bold"
                style={styles.headerText}
                useTranslationText={false}
              />
            </View>
            <NewCustomActionButton
              text="Invite Students"
              onPress={() => setModalState({ type: DegreeRosterModalState.INVITE_STUDENTS })}
              leftIcon={<PlusCircle color={primaryColor} />}
            />
          </View>
          <View style={styles.headerRow}>
            <View style={styles.subHeaderContainer}>
              <CustomText text="Total Students" size="m" weight="semiBold" style={styles.subHeaderText} />
              <CustomText
                text={`(${getStudentsByDegreeIdData?.getStudentsByDegreeId?.count || 0})`}
                size="m"
                weight="semiBold"
                style={styles.subHeaderText}
                useTranslationText={false}
              />
              {selectedStudents.length > 0 && (
                <CustomButton
                  text={`Remove Selected`}
                  styleType="danger"
                  onPress={() => setModalState({ type: DegreeRosterModalState.REMOVE_STUDENTS })}
                  leftIcon={<MinusCircleIcon />}
                  rightIcon={null}
                  style={{}}
                  textStyle={{ color: "#fff", fontWeight: "bold" }}
                  disabled={false}
                  textProps={{}}
                />
              )}
            </View>
            <View style={styles.controlsContainer}>
              <SortingDropdown
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortDropdownOpen={sortDropdownOpen}
                setSortDropdownOpen={setSortDropdownOpen}
                sortItems={SORT_ITEMS}
                label="Sort by"
              />
              <CustomSearchInput searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
            </View>
          </View>
        </View>
        <DegreeRosterTable
          students={getStudentsByDegreeIdData?.getStudentsByDegreeId.data || []}
          setSelectedStudents={setSelectedStudents}
          selectedStudents={selectedStudents}
        />
        {totalItemsCount > 0 && (
          <TableControlsContainer
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalCount={totalItemsCount}
          />
        )}
      </View>
      <NewCustomAxioActionModal
        visible={modalState.type === DegreeRosterModalState.REMOVE_STUDENTS}
        onClose={handleModalClose}
        onAction={handleRemoveStudents}
        modalTexts={modals.removeStudents}
      />
      <NewCustomAxioActionModal
        visible={modalState.type === DegreeRosterModalState.REMOVE_CONFIRMATION}
        onClose={handleModalClose}
        modalTexts={modals.removeConfirmation}
      />
      <InviteStudentModalContainer
        visible={modalState.type === DegreeRosterModalState.INVITE_STUDENTS}
        onClose={handleModalClose}
        onSubmit={inviteStudents}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 24,
    backgroundColor: "#fff",
    borderRadius: 8,
    flex: 1,
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  backText: {
    color: "#000",
  },
  header: {
    flexDirection: "column",
    width: "100%",
    marginBottom: 24,
    zIndex: 3000,
    elevation: 3000,
  },
  headerRow: {
    flexDirection: "row",
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 16,
    zIndex: 3000,
    elevation: 3000,
  },
  headerText: {
    color: "#000",
  },
  subHeaderContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  subHeaderText: {
    color: "#000",
  },
  controlsContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    zIndex: 3000,
    elevation: 3000,
  },
});

export default DegreeRosterTabContainer;
